import { SectionStatusServant } from './section_status_servant';
import { SectionRegistrationServant } from './section_registration_servant';
import { ScheduledSectionServant } from './scheduled_section_servant';
import { TermCourseServant } from '../../term_courses/servants/term_course_servant';
import { ISection, ISectionType } from '../../../interfaces/scheduler_interfaces';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { ITermCourse } from '../../../interfaces/scheduler_interfaces';
import { ScheduledSectionStatus } from '../types/scheduled_section_status_enum';
import { ITermSectionTypesDatasource } from '../../term_courses/datasources/term_section_types_datasource';
import { ScheduledSectionStatusServant } from './scheduled_section_status_servant';
const _4 = require('lodash4');

export class MultiSectionServant {
  constructor(
    private ngSectionsDatasource: ISectionsDatasource,
    private ngScheduledSectionStatusServant: ScheduledSectionStatusServant,
    private ngTermSectionTypesDatasource: ITermSectionTypesDatasource,
    private ngScheduledSectionServant: ScheduledSectionServant
  ) {}

  // TODO: do we need to add here the logic to confirm if courseHasMultipleSectionTypesActive as requisite to be multi-section?
  isRegisteredMultiSection(section: ISection): boolean {
    return TermCourseServant.isMultiSectionCourse(section._course) && SectionStatusServant.isStatusRegistered(section);
  }

  getAllSectionsForThisCourseId(courseId: number): ISection[] {
    return _4.filter(this.ngSectionsDatasource.read(), (scheduledSection: ISection) =>
      _4.isEqual(scheduledSection.course, courseId)
    );
  }

  getAllRequiredSectionsForThisCourseId(courseId: number): ISection[] {
    return _4.filter(
      this.ngSectionsDatasource.read(),
      (scheduledSection: ISection) =>
        _4.isEqual(scheduledSection.course, courseId) && _4.get(scheduledSection, '_section_type.is_required', false)
    );
  }

  getSiblingSections(section: ISection) {
    if (!section) {
      return [];
    }
    const siblingSectionsOfSameCourse = _4.filter(
      this.ngSectionsDatasource.read(),
      (sctn: ISection) => sctn.course === section.course
    );
    return siblingSectionsOfSameCourse;
  }

  getScheduledSiblingSections(section: ISection) {
    return SectionRegistrationServant.getScheduledSiblingSections(
      section,
      this.ngSectionsDatasource.values(),
      this.ngTermSectionTypesDatasource.values()
    );
  }

  getScheduledSiblingSectionsOfSameStatus(section: ISection) {
    const siblingSectionsOfSameStatus = _4.filter(
      this.getScheduledSiblingSections(section),
      (sctn) => sctn.status === section.status
    );
    return siblingSectionsOfSameStatus;
  }

  areAllMultiSectionsRegistered(section: ISection, sectionsWithStatus: ISection[]) {
    return _4.size(this.getTiedNonRegisteredRequiredSections(section, sectionsWithStatus)) === 0;
  }

  getRegisteredSiblings(section: ISection, sectionsWithStatus: ISection[]): ISection[] {
    if (!TermCourseServant.isMultiSectionCourse(section._course)) {
      return [];
    }
    return _4.filter(
      sectionsWithStatus,
      (scheduledSection: ISection) =>
        _4.isEqual(scheduledSection.course, section.course) &&
        !_4.isEqual(scheduledSection.id, section.id) &&
        SectionStatusServant.isStatusRegistered(scheduledSection)
    );
  }

  getTiedNonRegisteredRequiredSections(section: ISection, sectionsWithStatus: ISection[]): ISection[] {
    if (!TermCourseServant.isMultiSectionCourse(section._course)) {
      return [];
    }
    const sectionTypeIDsAlreadyRegistered = _4(this.getRegisteredSiblings(section, sectionsWithStatus))
      .map('section_type')
      .value();
    return _4.filter(
      sectionsWithStatus,
      (scheduledSection: ISection) =>
        _4.isEqual(scheduledSection.course, section.course) &&
        !_4.isEqual(scheduledSection.id, section.id) &&
        _4.isEqual(
          _4.get(scheduledSection, '_section_type.session', ''),
          _4.get(section, '_section_type.session', '')
        ) &&
        !this.ngScheduledSectionStatusServant.isStatusRegistered(scheduledSection) &&
        !_4.includes(sectionTypeIDsAlreadyRegistered, scheduledSection.section_type)
    );
  }

  getSiblingWaitlistedSections(section: ISection, waitListedSections: ISection[]): ISection[] {
    const waitListStatuses = ScheduledSectionStatus.getWaitlistedSectionStatuses();
    return _4.filter(
      waitListedSections,
      (scheduledSection: ISection) =>
        _4.isEqual(scheduledSection.course, section.course) &&
        !_4.isEqual(scheduledSection.id, section.id) &&
        _4.includes(waitListStatuses, scheduledSection.status)
    );
  }

  // TODO: this method is called from a function which is not called from anywhere
  getRequiredNonScheduledAndWaitlistedSections(course: ITermCourse) {
    const sectionsForCourse = _4(this.getAllSectionsForThisCourseId(course.id)).value();
    const sectionTypesNotScheduled = _4(sectionsForCourse)
      .filter({ status: '' })
      .map('section') // TODO: don't see this attr in builder/meta, there is something wrong about it
      .uniq()
      .value();
    const waitListStatuses = ScheduledSectionStatus.getWaitlistedSectionStatuses();
    const nonScheduledOrWaitlistedSections = _4.filter(sectionsForCourse, (sectionForCourse: any) => {
      return (
        _4.includes(waitListStatuses, sectionForCourse.status) ||
        (_4.isEqual(sectionForCourse.status, '') &&
          _4.includes(sectionTypesNotScheduled, sectionForCourse.section_type))
      );
    });
    return nonScheduledOrWaitlistedSections;
  }

  hasUnscheduledRequiredSiblingSections(section: ISection, allScheduledSections: ISection[]): boolean {
    return SectionRegistrationServant.hasUnscheduledRequiredSiblingSections(section, allScheduledSections);
  }

  getUnscheduledOptionalSiblingSectionTypes(course: ITermCourse, courseIdsWithSessions: any): ISectionType[] {
    if (!TermCourseServant.isMultiSectionCourse(course)) {
      return [];
    }
    const allSectionTypesForCourse = course._section_types;
    const sessionsScheduled = courseIdsWithSessions[course.id];
    const optionalSectionTypesForCourse = _4.filter(
      allSectionTypesForCourse,
      (secType: ISectionType) => !secType.is_required && _4.includes(sessionsScheduled, secType.session)
    );

    const scheduledSectionsForCourse = _4.filter(
      this.getAllSectionsForThisCourseId(course.id),
      (scheduledSection: ISection) => _4.isEqual(scheduledSection.status, ScheduledSectionStatus.SECTION_PICKED)
    );
    const optionalSectionTypesOfScheduledSections: ISectionType[] = _4(scheduledSectionsForCourse)
      .map((sec: ISection) => sec.section_type)
      .map((sectionTypeId: number) => this.ngTermSectionTypesDatasource.get(sectionTypeId))
      .compact()
      .filter((secType: ISectionType) => !secType.is_required)
      .value() as ISectionType[];

    const unscheduledOptionalSiblingSectionTypes: ISectionType[] = _4.differenceBy(
      optionalSectionTypesForCourse,
      optionalSectionTypesOfScheduledSections,
      (st: ISectionType) => st.id
    );
    return unscheduledOptionalSiblingSectionTypes;
  }
  hasUnscheduledOptionalSiblingSections(section: ISection, allScheduledSections: ISection[]): boolean {
    if (!TermCourseServant.isMultiSectionCourse(section._course)) {
      return false;
    }
    const allSectionTypesForCourse = section._course._section_types;
    const optionalSectionTypesForCourse = _4.filter(
      section._course._section_types,
      (secType: ISectionType) =>
        !secType.is_required && secType.session === _4.toString(_4.get(section, '_section_type.session'))
    );

    const scheduledSectionsForCourse = _4.filter(
      allScheduledSections,
      (scheduledSection: ISection) =>
        _4.isEqual(scheduledSection.course, section.course) &&
        !_4.isEqual(scheduledSection.status, ScheduledSectionStatus.UNSCHEDULED)
    );

    const optionalSectionTypesOfScheduledSections = _4(scheduledSectionsForCourse)
      .uniqBy('section_type')
      .map('_section_type')
      .filter((secType: ISectionType) => !secType.is_required)
      .value();

    const unscheduledOptionalSiblingSectionTypes = _4.differenceBy(
      optionalSectionTypesForCourse,
      optionalSectionTypesOfScheduledSections,
      (st: ISectionType) => st.id
    );

    return _4.gt(unscheduledOptionalSiblingSectionTypes.length, 0);
  }

  hasWaitlistedSiblingSections(section: ISection, allScheduledSections: ISection[]): boolean {
    if (!TermCourseServant.isMultiSectionCourse(section._course)) {
      return false;
    }

    const waitlistedSiblingSections = _4.filter(
      allScheduledSections,
      (scheduledSection: ISection) =>
        _4.isEqual(scheduledSection.course, section.course) &&
        _4.isEqual(scheduledSection._section_type.session, section._section_type.session) &&
        !_4.isEqual(scheduledSection.id, section.id) &&
        _4.isEqual(scheduledSection.status, ScheduledSectionStatus.WAITLISTED) &&
        !this.ngScheduledSectionServant.canAddFromWaitlist(scheduledSection)
    );

    return !_4.isEmpty(waitlistedSiblingSections);
  }
  hasTiedMultiSectionUnscheduledRequiredSections(section: ISection, scheduledSections: ISection[]): boolean {
    return this.hasUnscheduledRequiredSiblingSections(section, scheduledSections);
  }

  getTruncatedSectionTypeName(section: ISection, charLimit: number = 25): string {
    return _4.truncate(_4.get(section, `_section_type.name`, ''), { length: charLimit });
  }

  getMenuTitle(section: ISection): string {
    const siblingSectionsOfSameStatus = this.getScheduledSiblingSectionsOfSameStatus(section);
    const noOfSiblings = _4.size(siblingSectionsOfSameStatus);
    const areSiblingsSameStatus = noOfSiblings === _4.size(section._course._section_types);
    const menuMultiSecTitle =
      (areSiblingsSameStatus ? 'All' : `(${noOfSiblings})`) + ` Section${noOfSiblings > 1 ? 's' : ''}`;
    return menuMultiSecTitle;
  }

  getSelectedSwapIds(swapIDs: number[]) {
    return '&selected_swap_id=' + _4.reduce(swapIDs, (sum: any, item: number) => `${sum}&selected_swap_id=${item}`);
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/servants/multi_section_servant.ts